import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../index'

export interface GuestTourApplication {
  id: number
  uuid: string
  name: string
  email: string
  company: string
  role: string
  project_description: string
  team_size: string | null
  wants_call: number
  wants_guest_access: number
  invitation_id: number | null
  invitation_uuid: string | null
  invitation_url: string | null
  status: string
  notes: string | null
  created_at: string
  updated_at: string
}

export interface GuestTourFilters {
  page: number
  limit: number
  search: string
  status: string
  sortBy: string
  sortOrder: 'ASC' | 'DESC'
}

export interface GuestTourStats {
  totalApplications: number
  newApplications: number
  contactedApplications: number
  demoSentApplications: number
  completedApplications: number
  wantsCallCount: number
  wantsGuestAccessCount: number
}

export const guestToursApi = createApi({
  reducerPath: 'guestToursApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/admin',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['GuestTour'],
  endpoints: (builder) => ({
    getGuestTours: builder.query<
      { data: GuestTourApplication[]; meta: any },
      Partial<GuestTourFilters>
    >({
      query: (filters) => ({
        url: '/guest-tours',
        params: filters,
      }),
      providesTags: ['GuestTour'],
    }),
    getGuestTourStats: builder.query<{ data: GuestTourStats }, void>({
      query: () => '/guest-tours/stats',
      providesTags: ['GuestTour'],
    }),
    updateGuestTourStatus: builder.mutation<void, { id: number; status: string }>({
      query: ({ id, status }) => ({
        url: `/guest-tours/${id}/status`,
        method: 'PATCH',
        body: { status },
      }),
      invalidatesTags: ['GuestTour'],
    }),
    updateGuestTourNotes: builder.mutation<void, { id: number; notes: string }>({
      query: ({ id, notes }) => ({
        url: `/guest-tours/${id}/notes`,
        method: 'PATCH',
        body: { notes },
      }),
      invalidatesTags: ['GuestTour'],
    }),
    deleteGuestTour: builder.mutation<void, number>({
      query: (id) => ({
        url: `/guest-tours/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['GuestTour'],
    }),
  }),
})

export const {
  useGetGuestToursQuery,
  useGetGuestTourStatsQuery,
  useUpdateGuestTourStatusMutation,
  useUpdateGuestTourNotesMutation,
  useDeleteGuestTourMutation,
} = guestToursApi

